import { useMemo } from "react";
import { useGetContactStats } from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { format, subDays, startOfDay } from "date-fns";

const DAYS = 30;

/** GitHub-style Less→More intensity buckets. */
function levelFor(count: number): 0 | 1 | 2 | 3 | 4 {
  if (count <= 0) return 0;
  if (count === 1) return 1;
  if (count === 2) return 2;
  if (count === 3) return 3;
  return 4;
}

const LEVEL_CLASSES: Record<0 | 1 | 2 | 3 | 4, string> = {
  0: "bg-muted",
  1: "bg-green-200",
  2: "bg-green-400",
  3: "bg-green-600",
  4: "bg-green-800",
};

/**
 * Buckets the last DAYS (30) days, ending today, into columns of up to 7
 * consecutive days each (a compact ~5-column grid). The final column may be
 * short since DAYS need not be an exact multiple of 7.
 */
function buildWeekColumns(
  countByDate: Map<string, number>,
): { date: Date; count: number }[][] {
  const today = startOfDay(new Date());
  const days: { date: Date; count: number }[] = [];
  for (let i = DAYS - 1; i >= 0; i--) {
    const date = subDays(today, i);
    const key = format(date, "yyyy-MM-dd");
    days.push({ date, count: countByDate.get(key) ?? 0 });
  }
  const columns: { date: Date; count: number }[][] = [];
  for (let w = 0; w * 7 < DAYS; w++) {
    columns.push(days.slice(w * 7, Math.min(w * 7 + 7, DAYS)));
  }
  return columns;
}

function HeatmapSkeleton() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-4 w-40" />
      <Skeleton className="h-28 w-full rounded-md" />
    </div>
  );
}

/**
 * Member profile enrichment (Phase 38) — GitHub-style last-30-days attendance
 * heatmap + "N this month · N day streak" header, consuming
 * `GET /contacts/:id/stats` (useGetContactStats). thisMonthVisits/dayStreak
 * are server-computed; this component only buckets the last 30 days of
 * stats.heatmap into day-columns for rendering. Plain divs/grid (no
 * useReactTable — standing pitfall).
 */
export function AttendanceHeatmap({ contactId }: { contactId: number }) {
  const { data: stats, isLoading } = useGetContactStats(contactId);

  const columns = useMemo(() => {
    const countByDate = new Map<string, number>();
    for (const cell of stats?.heatmap ?? []) {
      countByDate.set(format(new Date(cell.date), "yyyy-MM-dd"), cell.count);
    }
    return buildWeekColumns(countByDate);
  }, [stats]);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-sm font-semibold">Attendance</CardTitle>
          {!isLoading && stats ? (
            <p className="text-xs text-muted-foreground">
              {stats.thisMonthVisits} this month · {stats.dayStreak} day streak
            </p>
          ) : null}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <HeatmapSkeleton />
        ) : (
          <>
            <div className="flex gap-[3px] overflow-x-auto">
              {columns.map((week, wi) => (
                <div key={wi} className="flex flex-col gap-[3px]">
                  {week.map((day, di) => (
                    <div
                      key={di}
                      title={`${format(day.date, "d MMM yyyy")} · ${day.count} visit${day.count === 1 ? "" : "s"}`}
                      className={cn(
                        "h-3 w-3 rounded-sm",
                        LEVEL_CLASSES[levelFor(day.count)],
                      )}
                    />
                  ))}
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-center justify-between">
              <p className="text-xs text-muted-foreground">Last 30 days</p>
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <span>Less</span>
                {([0, 1, 2, 3, 4] as const).map((lvl) => (
                  <span
                    key={lvl}
                    className={cn("h-3 w-3 rounded-sm", LEVEL_CLASSES[lvl])}
                  />
                ))}
                <span>More</span>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
